export type KeyerMode = 'paddle' | 'straight'

export type KeyInput = 'dit' | 'dah' | 'straight'

export type MorseElement = '.' | '-'

export interface KeyBindings {
  dit: KeyboardEvent['code']
  dah: KeyboardEvent['code']
  straight: KeyboardEvent['code']
}

export interface HeldInputs {
  dit: boolean
  dah: boolean
  straight: boolean
}

export interface KeyerState {
  mode: KeyerMode
  held: HeldInputs
  sequence: string
  lastPaddleElement: MorseElement | null
  straightPressedAtMs: number | null
}

export type KeyerAction =
  | { type: 'press'; input: KeyInput; atMs: number }
  | { type: 'release'; input: KeyInput; atMs: number }
  | { type: 'paddleTick' }
  | { type: 'commit' }
  | { type: 'reset'; mode?: KeyerMode }

export interface KeyerTransition {
  state: KeyerState
  element?: MorseElement
  completedSequence?: string
}

export const DEFAULT_KEY_BINDINGS: Readonly<KeyBindings> = Object.freeze({
  dit: 'KeyF',
  dah: 'KeyJ',
  straight: 'Space',
})

const EMPTY_HELD_INPUTS: Readonly<HeldInputs> = Object.freeze({
  dit: false,
  dah: false,
  straight: false,
})

/**
 * The duration of one Morse timing unit (a dit) using the standard PARIS
 * definition of WPM. Invalid/non-positive values fall back to one WPM so
 * callers never have to schedule an infinite or negative timer.
 */
export function wpmToUnitMs(wpm: number): number {
  const safeWpm = Number.isFinite(wpm) && wpm > 0 ? wpm : 1
  return 1_200 / safeWpm
}

export function elementDurationMs(
  element: MorseElement,
  unitMs: number,
): number {
  return element === '.' ? unitMs : unitMs * 3
}

export function characterGapMs(unitMs: number): number {
  return unitMs * 3
}

/** Resolves a physical keyboard/HID code for the currently selected keyer. */
export function inputForKeyboardCode(
  mode: KeyerMode,
  code: KeyboardEvent['code'],
  bindings: KeyBindings,
): KeyInput | null {
  if (mode === 'straight') {
    return code === bindings.straight ? 'straight' : null
  }

  const isDit = code === bindings.dit
  const isDah = code === bindings.dah
  // A duplicated paddle binding is ambiguous, so ignore it until corrected.
  if (isDit === isDah) return null
  return isDit ? 'dit' : 'dah'
}

/** A straight-key closure shorter than two units is a dit; otherwise a dah. */
export function classifyStraightElement(
  durationMs: number,
  unitMs: number,
): MorseElement {
  return Math.max(0, durationMs) < unitMs * 2 ? '.' : '-'
}

/**
 * Chooses the next automatic-keyer element. Holding both paddles alternates
 * dits and dahs; a fresh squeeze starts with a dit.
 */
export function chooseNextPaddleElement(
  held: Pick<HeldInputs, 'dit' | 'dah'>,
  lastElement: MorseElement | null,
): MorseElement | null {
  if (held.dit && held.dah) {
    return lastElement === '.' ? '-' : '.'
  }

  if (held.dit) return '.'
  if (held.dah) return '-'
  return null
}

export function createKeyerState(mode: KeyerMode): KeyerState {
  return {
    mode,
    held: { ...EMPTY_HELD_INPUTS },
    sequence: '',
    lastPaddleElement: null,
    straightPressedAtMs: null,
  }
}

function isInputForMode(mode: KeyerMode, input: KeyInput): boolean {
  return mode === 'paddle' ? input !== 'straight' : input === 'straight'
}

/**
 * Deterministic keyer state transition. Time is supplied by the caller so the
 * complete input classification logic can be unit-tested without fake clocks.
 */
export function transitionKeyer(
  state: KeyerState,
  action: KeyerAction,
  unitMs: number,
): KeyerTransition {
  if (action.type === 'reset') {
    return { state: createKeyerState(action.mode ?? state.mode) }
  }

  if (action.type === 'commit') {
    if (!state.sequence) return { state }

    return {
      state: {
        ...state,
        sequence: '',
        lastPaddleElement: null,
      },
      completedSequence: state.sequence,
    }
  }

  if (action.type === 'paddleTick') {
    if (state.mode !== 'paddle') return { state }

    const element = chooseNextPaddleElement(
      state.held,
      state.lastPaddleElement,
    )
    if (!element) return { state }

    return {
      state: {
        ...state,
        sequence: state.sequence + element,
        lastPaddleElement: element,
      },
      element,
    }
  }

  if (!isInputForMode(state.mode, action.input)) return { state }

  if (action.type === 'press') {
    if (state.held[action.input]) return { state }

    return {
      state: {
        ...state,
        held: { ...state.held, [action.input]: true },
        straightPressedAtMs:
          action.input === 'straight'
            ? action.atMs
            : state.straightPressedAtMs,
      },
    }
  }

  if (!state.held[action.input]) return { state }

  if (action.input !== 'straight') {
    return {
      state: {
        ...state,
        held: { ...state.held, [action.input]: false },
      },
    }
  }

  const startedAt = state.straightPressedAtMs ?? action.atMs
  const element = classifyStraightElement(action.atMs - startedAt, unitMs)

  return {
    state: {
      ...state,
      held: { ...state.held, straight: false },
      sequence: state.sequence + element,
      straightPressedAtMs: null,
    },
    element,
  }
}
